import { Dayjs } from 'dayjs';

import { SBaseDisplay, SDate } from '@summit/core';

import { DateRangeType, NumberFormatter, SummitFormattedText, Week } from '@summit/models';
import { EboEvent } from '../../base-classes/ebo-event';
import { Employee } from '../../employee/employee';
import { Company } from '../../organization/company/company';
import { ServiceArea } from '../../organization/service-area/service-area';
import { PayType, PayTypeMapping } from '../../payroll/shared/pay-type.enum';
import { Hub } from '../../places/hub/hub';
import { Reconcile } from '../../reconcile/reconcile';
import { CompanySettlement } from '../../settlements/company-settlement';
import { DigitalLogType } from '../../shared/digital-log-type.enum';
import { SpotRun } from '../../spot/spot-run';
import { TruckAlias } from '../../truck/truck-alias';
import { ILeg } from '../i-leg';
import { SettlementItemDetail } from './settlement-item-detail';

export class Settlement extends EboEvent implements SBaseDisplay, ILeg {
  name: string;
  companySettlementID: string;
  companySettlement: CompanySettlement;
  settlementDate: Dayjs;
  dispatchDate: Dayjs;
  employeeID: string;
  employee?: Employee;
  fedExIDTeammate?: string;
  logType: DigitalLogType;
  // teammateId?: string;
  // teammate?: Employee;
  truckAliasId: string;
  truckAlias?: TruckAlias;
  truckNum: string;
  origNum: number;
  origHub: Hub;
  destNum: number;
  destHub: Hub;
  spotRunId: string;
  spotRun: SpotRun;
  miles: number;
  run?: number;
  payTypeID: PayType;
  settlementCompanyID?: string;
  settlementCompany?: Company;
  reconcileID?: string;
  reconcile?: Reconcile;
  vmrRate?: number;
  mileagePlusRate?: number;
  fuelRate?: number;
  dropAndHooks?: number;
  grossAmount?: number;
  spotAmount?: number;
  flatRate?: number;
  details?: SettlementItemDetail[];
  //Load Info
  serviceAreaId: string;
  serviceArea: ServiceArea;
  packagePickupCount: number;
  packagePickupAmount: number;
  stopPickupCount: number;
  stopPickupAmount: number;
  packageDeliveredCount: number;
  packageDeliveredAmount: number;
  stopDeliveredCount: number;
  stopDeliveredAmount: number;
  ecommDlPackages?: number;
  ecommDlStops?: number;

  //List properties to speed up access and avoid deep property checks in templates
  contractName?: string;
  contractExternalCompany?: Company;

  get fedExID(): string {
    return this.employee?.fedExID;
  }

  get fuelSupplement(): number {
    return Math.ceil((this.miles ?? 0) * (this.fuelRate ?? 0) * 100) / 100;
  }

  get nonFuelGross(): number {
    return (this.grossAmount ?? 0) - (this.fuelSupplement ?? 0);
  }

  get destAbbr(): string {
    switch (this.logType) {
      case DigitalLogType.FedExLinehaul:
        return this.destNum?.toString() ?? '--';
      case DigitalLogType.FedExSpot:
        return this.spotRun ? `#${this.spotRun.spotNumber}` : '--';
      default:
        return '--';
    }
  }

  static SortByTravel(inList: Settlement[], startHub: number): Settlement[] {
    if (inList.length <= 1) return inList;
    if (inList.length == 2) {
      if (inList[0].origNum == startHub) return inList;
      return inList.reverse();
    }
    const inputList = [...inList];
    let responseList = new Array<Settlement>();
    let nextLegIndex = inputList.findIndex((entry) => entry.origNum == startHub);
    if (nextLegIndex < 0) nextLegIndex = 0;
    const nextLegs = inputList.splice(nextLegIndex, 1);
    responseList.push(nextLegs[0]);
    const remainingLegs = Settlement.SortByTravel(inputList, nextLegs[0].destNum);
    responseList = responseList.concat(remainingLegs);
    return responseList;
  }
  constructor(jsonData?: Partial<Settlement>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.employee) {
        this.employee = new Employee(jsonData.employee);
      }
      if (jsonData.companySettlement) this.companySettlement = new CompanySettlement(jsonData.companySettlement);
      if (jsonData.origHub) this.origHub = new Hub(jsonData.origHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
      if (jsonData.settlementDate) this.settlementDate = SDate.newUtc(jsonData.settlementDate);
      if (jsonData.dispatchDate) this.dispatchDate = SDate.newUtc(jsonData.dispatchDate);
      if (jsonData.truckAlias) this.truckAlias = new TruckAlias(jsonData.truckAlias);
      if (jsonData.settlementCompany) this.settlementCompany = new Company(jsonData.settlementCompany);
      if (jsonData.spotRun) this.spotRun = new SpotRun(jsonData.spotRun);
      if (jsonData.serviceArea) this.serviceArea = new ServiceArea(jsonData.serviceArea);
      if (jsonData.details) this.details = jsonData.details.map((item) => new SettlementItemDetail(item));
      if (jsonData.reconcile) {
        this.reconcile = new Reconcile(jsonData.reconcile);
      }
      if (jsonData.contractExternalCompany)
        this.contractExternalCompany = new Company(jsonData.contractExternalCompany);
    }
  }

  toShortString(): string {
    return this.name;
  }

  isEditable(/*noUnusedParameters ux: IUserExperience*/): boolean {
    return !this.reconcileID;
  }

  get contractSummary(): string {
    const companyName = this.settlementCompany?.name || '';
    const contractName = this.contractName || '';

    if (companyName && contractName) {
      return `${companyName}: ${contractName}`;
    }

    if (companyName || contractName) {
      return companyName || contractName;
    }

    return '--';
  }

  get origName(): string {
    return this.origHub?.toShortString() ?? '--';
  }

  private _destName: SummitFormattedText;
  get destName(): SummitFormattedText {
    if (!this._destName) {
      this._destName = new SummitFormattedText();
      switch (this.logType) {
        case DigitalLogType.FedExLinehaul:
          this._destName.AddLine(this.destHub?.toShortString() ?? '--');
          break;
        case DigitalLogType.FedExSpot:
          this._destName.AddLine(this.spotRun?.toShortString() ?? '--');
          break;
        case DigitalLogType.FedExPnD: {
          if (this.serviceArea)
            this._destName.AddLine(this.serviceArea?.toShortString(), ['font-bold', 'w-full', 'text-center']);
          if (this.stopPickupCount) this._destName.AddKeyValues('PU Stops', NumberFormatter(this.stopPickupCount));
          if (this.stopDeliveredCount)
            this._destName.AddKeyValues('DL Stops', NumberFormatter(this.stopDeliveredCount));
          if (this.ecommDlStops) this._destName.AddKeyValues('EComm Stops', NumberFormatter(this.ecommDlStops));
          break;
        }
        default:
          this._destName.AddLine('--');
          break;
      }
    }
    return this._destName;
  }

  get payTypeSummary(): string {
    switch (this.logType) {
      case DigitalLogType.FedExLinehaul:
        return PayTypeMapping[this.payTypeID];
      case DigitalLogType.FedExSpot:
        return 'Spot';
      default:
        return PayTypeMapping[this.payTypeID];
    }
  }

  private _workSummary: SummitFormattedText;
  get workSummary(): SummitFormattedText {
    if (this._workSummary === undefined) {
      this._workSummary = new SummitFormattedText();
      this._workSummary.AddLine(this.payTypeSummary);
      if (this.payTypeID == PayType.Team) {
        const selWeek = new Week(this.eventDate, DateRangeType.SettlementWeek);
        const dateRange = selWeek.ToDateRange(DateRangeType.SettlementWeek);
        this._workSummary.AddLine(this.fedExIDTeammate, [], {
          path: ['/settlements'],
          queryParams: {
            WeekRange: JSON.stringify({ filterData: dateRange }),
            filter: this.fedExIDTeammate
          },
          target: '_blank'
        });
      }
    }
    return this._workSummary;
  }

  private _detailSummary: SummitFormattedText;
  get detailSummary(): SummitFormattedText {
    if (!this._detailSummary) {
      this._detailSummary = new SummitFormattedText();
      this.details?.forEach((detail) => {
        this._detailSummary.AddText(detail.toFormattedText());
      });
      if (this.logType === DigitalLogType.FedExPnD) {
        if (this.packagePickupCount)
          this._detailSummary.AddKeyValues('Pickup Pkg', NumberFormatter(this.packagePickupCount));
        if (this.packageDeliveredCount)
          this._detailSummary.AddKeyValues('Delivered Pkg', NumberFormatter(this.packageDeliveredCount));
        if (this.ecommDlPackages)
          this._detailSummary.AddKeyValues('EComm Packages', NumberFormatter(this.ecommDlPackages));
      }
    }
    return this._detailSummary;
  }
}
